// @mui
import { Box, Button, Card, Grid, Typography } from "@mui/material";
import PageLayout from "layouts/PageLayout";

import WorkIcon from '@mui/icons-material/Work';
import dayjs from "dayjs";

import { ButtonGroup, HoverLoader, LinearProgressWithLabel } from "components";
import { Select } from "components/forms";
import { AppWidgetSummary } from "sections/dashboard/AppWidgetSummary";
import { useEffect, useState } from "react";
import { services } from "redux/services";
import { useToastMessage } from "hooks";
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import { DefaultizedPieValueType } from "@mui/x-charts";
import { Book, HealthAndSafety, MedicalInformation, Refresh, Search } from "@mui/icons-material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { genderList } from "utils/constants";
import StatCard from "layouts/dashboard/StatCard";

const StyledText = styled('text')(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: 20,
}));


function PieCenterLabel({ children }: { children: React.ReactNode }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <StyledText x={left + width / 2} y={top + height / 2}>
      {children} Taken
    </StyledText>
  );
}
const getArcLabel = (params: DefaultizedPieValueType) => {
  return `${params.value}%`;
};


export default function DashboardApp() {
  const { toastMessage } = useToastMessage();
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<any>({
    start_date: null,
    end_date: null,
    gender: null
  });

  const [dashboard, setDashboard] = useState<any>({
    user: 0,
    medication: 0,
    symptoms: 0,
    surveys: 0,
    adherence_rates: [
      {
        "label": "Male",
        "value": 0
      },
      {
        "label": "Female",
        "value": 0
      },
      {
        "label": "Other",
        "value": 0
      },
    ],
    user_weekly_data: null,
    tracker_summary: [
      {
        "label": "On Time",
        "value": 0
      },
      {
        "label": "Missed",
        "value": 0
      },
      {
        "label": "Late",
        "value": 0
      },
      {
        "label": "Early",
        "value": 0
      }
    ]
  });

  useEffect(() => {
    apiCall(null);
  }, []);


  const apiCall = (params: any) => {
    setLoading(true);
    services
      .dashboard(params)
      .then((res: any) => {
        const response = res?.result;
        setDashboard(response);
      })
      .catch((err) => {
        toastMessage(JSON.stringify(err?.data?.message) || "Something wrong !", 'error');
      }).finally(() => setTimeout(() => {
        setLoading(false);
      }, 1000));
  }

  const handleSearch = () => {
    if ((filter.start_date && filter.end_date) || filter.gender) {
      const params = { ...filter };
      params.start_date = params.start_date ? dayjs(params.start_date).format('YYYY-MM-DD') : null;
      params.end_date = params.end_date ? dayjs(params.end_date).format('YYYY-MM-DD') : null;
      params.gender = params.gender?.value;
      apiCall(params)
    } else {
      toastMessage('Please select start date, end date or gender!', 'warning');
    }
  }

  const handleFilter = (name: string, value: any) => {
    setFilter({
      ...filter,
      [name]: value
    })
  }

  const handleReset = () => {
    apiCall(null);
    setFilter({
      start_date: null,
      end_date: null,
      gender: null
    })
  }

  return (
    <PageLayout title="Dashboard" isList={false} hideToolbar>
      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12} md={6} lg={6}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DatePicker
              value={filter.start_date}
              onChange={(date: any) => handleFilter('start_date', date)}
              disableFuture
              slotProps={{ textField: { fullWidth: true, size: 'small' }, field: { clearable: true } }}
            />
            <DatePicker
              value={filter.end_date}
              onChange={(date: any) => handleFilter('end_date', date)}
              disableFuture
              slotProps={{ textField: { fullWidth: true, size: 'small' }, field: { clearable: true } }}
              minDate={filter.start_date}
            />
            <Select
              options={genderList}
              placeholder={`Gender`}
              value={filter.gender}
              size="small"
              onChange={(_, data) => handleFilter('gender', data)}
            />
          </Box>

        </Grid>


        <Grid item xs={12} md={6} lg={6}>
          <ButtonGroup>
            <Button
              color="error"
              size='large'
              variant='contained'
              onClick={(handleReset)}
              startIcon={<Refresh />}
            >
              Reset
            </Button>
            <Button
              size='large'
              variant='contained'
              onClick={handleSearch}
              startIcon={<Search />}
            >
              Search
            </Button>
          </ButtonGroup>
        </Grid>
      </Grid>
      {loading && <HoverLoader />}
      {/* <Typography variant="h6" color="secondary" marginBottom={2}>
        Summary
      </Typography> */}
      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatCard
            title="Total Users"
            value={dashboard?.user}
            percentChange="+25%"
            icon={<WorkIcon />}
            to={`/admin/users`}
          />
          {/* <AppWidgetSummary
            title="Total User"
            total={dashboard?.user}
            color="secondary"
            icon={<WorkIcon />}
            to={`/admin/users`}
          /> */}
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatCard
            title="Total Medications"
            value={dashboard?.medication}
            percentChange="+12%"
            icon={<MedicalInformation />}
            to={`/admin/settings/medications`}
          />
          {/* <AppWidgetSummary
            title="Total Medication"
            total={dashboard?.medication}
            color="primary"
            icon={<MedicalInformation />}
            to={`/admin/settings/medications`}
          /> */}
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatCard
            title="Total Symptoms Reported"
            value={dashboard?.symptoms}
            percentChange="+5%"
            icon={<HealthAndSafety />}
            to={'#'}
          />
          {/* <AppWidgetSummary
            title="Total Symptoms Reported"
            total={dashboard?.symptoms}
            color="error"
            icon={<HealthAndSafety />}
            to={'#'}
          /> */}
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatCard
            title="Total Surveys Taken"
            value={dashboard?.surveys}
            percentChange="+15%"
            icon={<Book />}
            to={'/admin/surveys'}
          />
          {/* <AppWidgetSummary
            title="Total Surveys Taken"
            total={dashboard?.symptoms}
            color="success"
            icon={<Book />}
            to={'/admin/surveys'}
          /> */}
        </Grid>
      </Grid>
      {/* <Typography variant="h6" color="secondary" marginBottom={2}>
        Report
      </Typography> */}
      <Grid container spacing={2} marginBottom={3}>
        {dashboard.user_weekly_data &&
          <Grid item xs={12} sm={12} md={7} lg={7}>
            <Card
              sx={{
                  background: '#eff4ff',
                border: '1px solid',
                borderRadius: '12px !important'
              }}
            >
              <BarChart
                height={400}
                series={[
                  { data: dashboard.user_weekly_data.maleData, label: 'Male', id: 'pvId', color: '#0E1458' },
                  { data: dashboard.user_weekly_data.femaleData, label: 'Female', id: 'uvId', color: '#f73378' },
                ]}
                xAxis={[{ data: dashboard.user_weekly_data.xLabels, scaleType: 'band' }]}
              />
            </Card>
          </Grid>
        }
        {dashboard.tracker_summary &&
          <Grid item xs={12} sm={12} md={5} lg={5}>
            <Card
            sx={{
              background: '#eff4ff',
            border: '1px solid',
            borderRadius: '12px !important'
          }}
            >
              <PieChart
                series={[{ data: dashboard?.tracker_summary, innerRadius: 60, arcLabel: getArcLabel }]}
                height={300}
              >
                <PieCenterLabel>{dashboard?.tracker_summary?.[0]?.value} %</PieCenterLabel>
              </PieChart>
              <Box sx={{ padding: 2 }}>
                {dashboard.adherence_rates?.map((ad: any, ind: number) =>
                  <Grid container key={ind}>
                    <Grid item xs={2}>
                      <Typography>{ad.label}</Typography>
                    </Grid>
                    <Grid item xs={10}>
                      <LinearProgressWithLabel sx={{ width: "100%", height: 20 }} value={ad.value} />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Card>
          </Grid>
        }
      </Grid>
    </PageLayout>
  );
}
